import { TakeOverTypeEnum } from '@outbound-ai/softphone'
import { useAppDispatch, useAppSelector } from 'appState/store'
import { MuteUnmuteMicrophone } from 'components/Buttons/MuteUnmuteMicrophone'
import { updateDialPadVisibility } from 'features/calls/callsSlice'
import { CallAgent } from 'features/calls/components'
import { CallStatusEnum, CallTypeToJobTypeEnum, ICall } from 'features/calls/types'
import { useUserPreference } from 'features/settings/settingsHooks'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { parseUserPhoneData } from 'features/settings/settingsUtils'
import { FaPhone, FaUser } from 'react-icons/fa'
import { MdDialpad } from 'react-icons/md'
import { Button, Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'

// components
// types
// store
interface IProps {
  onCloseCall: () => void
  onToggleMute: () => void
  onTakeOver: () => void
  isOwner: boolean
  call: ICall
}

const ChatActions = ({ onCloseCall, onToggleMute, onTakeOver, isOwner, call }: IProps) => {
  const { showDialPad, isMuted, isConnected, takeOver } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)

  const dispatch = useAppDispatch()

  const { data: userPhoneData } = useUserPreference(UserPreferencesKeysEnum.softphonePhoneNumbers)

  const isTakenOver = takeOver.type !== TakeOverTypeEnum.none
  const isBrowserHeadsetPhoneTakeOver = takeOver.type === TakeOverTypeEnum.browser

  const handleShowDialpad = () => {
    dispatch(updateDialPadVisibility(!showDialPad))
  }

  // variables
  const { userPhone } = parseUserPhoneData(userPhoneData)
  const callAgentDetailsMessage = () => {
    if (!isTakenOver) return 'AI Agent handling call'
    if ([TakeOverTypeEnum.dialed].includes(takeOver.type) && userPhone)
      return `You are speaking on the call via your ${userPhone.name}`
    if (isMuted) return 'You are muted'
    return 'You are speaking on the call'
  }

  // variables
  const isDisabledTakeOver = !isConnected || call?.status !== CallStatusEnum.InProgress
  const hasTakeOverFunctionality =
    call && [CallTypeToJobTypeEnum.attendable, CallTypeToJobTypeEnum.humanAgent].includes(call.jobType)
  const isDiagnosticCall = call?.jobType === CallTypeToJobTypeEnum.diagnosticHeadset

  return (
    <Section data-testid="call-chat-actions-container">
      <section className="left">
        <CallAgent Icon={<FaUser />} details={callAgentDetailsMessage()} />
      </section>

      {isOwner && (
        <section className="right">
          {isTakenOver
            ? isBrowserHeadsetPhoneTakeOver && (
                <>
                  <MuteUnmuteMicrophone isMuted={isMuted} toggleIsMuted={onToggleMute} />
                  {!isDiagnosticCall && (
                    <Tooltip
                      tooltipText={
                        showDialPad
                          ? `Click to hide the dial pad, or press (Ctrl+Shift+D)`
                          : `Click to open the dial pad, or press (Ctrl+Shift+D)`
                      }
                      position={PositionEnum.top}
                      positionContent={100}
                      positionTooltipArrow={30}
                      tooltipStyles={{ 'min-width': '10rem' }}
                    >
                      <Button onClick={handleShowDialpad} variant="primary" radius="rounded" className="dialpad-button">
                        <MdDialpad />
                      </Button>
                    </Tooltip>
                  )}
                </>
              )
            : hasTakeOverFunctionality && (
                <Tooltip
                  tooltipText="Remove the AI agent and takeover the remainder of this call."
                  position={PositionEnum.top}
                  positionContent={80}
                  positionTooltipArrow={20}
                  tooltipStyles={{ 'min-width': '12rem' }}
                >
                  <Button
                    variant="primary"
                    radius="rounded"
                    onClick={onTakeOver}
                    disabled={isDisabledTakeOver}
                    data-testid="take-over-btn"
                  >
                    Take Over
                  </Button>
                </Tooltip>
              )}

          <Button
            variant="danger"
            radius="rounded"
            onClick={onCloseCall}
            disabled={!isConnected}
            data-testid="end-call-btn"
          >
            <span className="rotate">
              <FaPhone />
            </span>
            End Call
          </Button>
        </section>
      )}
    </Section>
  )
}

const Section = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${({ theme }) => theme.util.pixelToRem(60)};
  border: 1px solid ${({ theme }) => theme.colors.grey.lighter};
  border-radius: ${({ theme }) => theme.util.pixelToRem(5)};
  padding: 0 0.75rem;

  .right {
    display: flex;

    button {
      margin-left: 1rem;

      &.dialpad-button {
        padding-block: 0.875rem;
      }

      &[data-testid='end-call-btn'] {
        padding-block: 0.5rem;
      }

      .rotate {
        margin-right: 0.75rem;
        svg {
          transform: rotate(225deg);
        }
      }
    }
  }
`

export { ChatActions }
